import type { LabelMatchSource, LabelProduct, LabelTaxonomy } from '../api/client.js';
import { gaugeLabel, putUpWithLength } from '../lib/products.js';

/**
 * The words on a Labeling row that let the person holding the product confirm
 * it is the right one. Pure.
 */

/**
 * The resolved taxonomy as one short line: series, model, color, then the size —
 * or, for a string, its gauge and put-up in place of a size. Parts that are not
 * resolved are left out; a part that only repeats the one before it is too (a
 * model that is the series name). Empty when nothing is resolved.
 */
export function taxonomyLine(taxonomy: LabelTaxonomy | null): string {
  if (taxonomy === null) return '';
  const parts: (string | null)[] = [taxonomy.series, taxonomy.model, taxonomy.color];
  if (taxonomy.category === 'string') {
    parts.push(taxonomy.gauge ? `Gauge ${gaugeLabel(taxonomy.gauge, taxonomy.gaugeMm)}` : null);
    parts.push(taxonomy.putUp ? putUpWithLength(taxonomy.putUp, taxonomy.putUpLength) : null);
  } else {
    parts.push(taxonomy.sizeLabel);
  }
  const out: string[] = [];
  for (const part of parts) {
    const text = part?.trim();
    if (!text) continue;
    if (out[out.length - 1]?.toLowerCase() === text.toLowerCase()) continue;
    out.push(text);
  }
  return out.join(' · ');
}

/** How a barcode was matched to the product; null for an entry that named it directly. */
export function matchedByText(source: LabelMatchSource): string | null {
  switch (source) {
    case 'upc':
      return 'Matched by the listing’s UPC';
    case 'booking':
      return 'Matched by a supplier booking line';
    case 'catalog':
      return 'Matched by Amazon’s catalog';
    default:
      return null;
  }
}

/** The name a row is announced and confirmed by: its title, else its seller SKU. */
export function productName(product: LabelProduct): string {
  return product.title?.trim() || product.sellerSku;
}
